import React, { useEffect, useMemo, useRef, useState } from "react"
import { Button, Modal } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { failureOf } from "utils/errMsgOf"
import { Editor as TiptapEditor } from "@tiptap/react"
import { useTemplateCatalogue } from "features/QuestionnaireEditor/api/useTemplates"
import { Loader } from "shared/ui/Loader/Loader.component"
import {
  StagedBlock,
  TemplateCatalogueItem,
  TemplateRef,
} from "types/template.types"
import { htmlToPlainText, stripCommentSpans } from "utils/editorUtils"
import { readQuestionnaireNames } from "utils/questionnaireNames"
import TemplateGallery from "./TemplateGallery.component"
import TemplateWizard from "./TemplateWizard.component"
import StagingTray from "./StagingTray.component"
import { sameRef, stagedBlockNames } from "./templateInputs"
import { useStagingTray } from "./useStagingTray"
import "./templates.css"

interface TemplatesDialogProps {
  open: boolean
  onClose: () => void
  /** The editor the templates go into (the wizard and tray insert at its cursor) */
  editor: TiptapEditor | null
  projectId: string
  /** The project owner (hint_owner of the template routes) */
  owner: string
  /** The project name, for the SURVEY line when the questionnaire has none */
  projectName: string
  /** The save that runs as the dialog opens (templates read the saved questionnaire) */
  saveState?: TemplatesSaveState
  /** Tries the save again after it failed */
  onRetrySave?: () => void
  /**
   * A block to open in its template's wizard as the dialog opens (an
   * assistant proposal), instead of the gallery
   */
  initialView?: TemplatesInitialView
  /** The initial view's block went into the staging tray (Add in its wizard) */
  onProposalStaged?: (blockId: string) => void
}

/** A template's wizard to open straight away, filled in with `block`. */
export interface TemplatesInitialView {
  ref: TemplateRef
  block: StagedBlock
}

/** Where the save made on opening the dialog is. */
export type TemplatesSaveState = "saving" | "saved" | "failed"

/**
 * What the dialog shows: the gallery, or the wizard for one template
 * (`editing` set when a staged block is opened again from the tray).
 */
export type TemplatesDialogView =
  | "gallery"
  | { wizard: TemplateCatalogueItem; editing?: StagedBlock }

/**
 * The Templates dialog (Insert > Template, and the start screen's Template
 * tile). It loads the catalogue while open and switches between the gallery
 * and the wizard. The staging tray sits under the gallery: Insert puts every
 * staged block in at the cursor as one edit, and closing with blocks staged
 * asks before they are dropped.
 *
 * @param props - The component props
 * @returns The dialog
 */
const TemplatesDialog: React.FC<TemplatesDialogProps> = ({
  open,
  onClose,
  editor,
  projectId,
  owner,
  projectName,
  saveState = "saved",
  onRetrySave,
  initialView,
  onProposalStaged,
}) => {
  const [view, setView] = useState<TemplatesDialogView>("gallery")
  // The initial view's template was not in the catalogue
  const [unavailable, setUnavailable] = useState(false)
  // The initial view already opened in this opening of the dialog
  const openedInitial = useRef<TemplatesInitialView | null>(null)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const [inserting, setInserting] = useState(false)
  const tray = useStagingTray(projectId, owner, editor, projectName)
  const stagedBlocks = tray.blocks
  const catalogue = useTemplateCatalogue(open)
  const items = useMemo(() => catalogue.data?.templates ?? [], [catalogue.data])

  // Closing with blocks staged asks first.
  const requestClose = () => {
    if (stagedBlocks.length > 0) setConfirmDiscard(true)
    else onClose()
  }

  const discard = () => {
    setConfirmDiscard(false)
    tray.clear()
    onClose()
  }

  const insert = async () => {
    setInserting(true)
    try {
      await tray.insert()
      onClose()
    } catch (e) {
      notify.toast.error(`The templates could not be inserted: ${failureOf(e)}`)
    } finally {
      setInserting(false)
    }
  }

  // A staged block opens its wizard again; the catalogue entry when there is
  // one, else enough of it for the wizard.
  const edit = (block: StagedBlock) =>
    setView({
      wizard: items.find((it) => sameRef(it, block.ref)) ?? {
        ...block.ref,
        category: "",
        label: block.title,
        description: "",
        inputs: [],
        merge: "",
      },
      editing: block,
    })

  // The questionnaire's names, read each time a wizard opens.
  const names = useMemo(
    () =>
      readQuestionnaireNames(
        view !== "gallery" && editor && !editor.isDestroyed
          ? htmlToPlainText(stripCommentSpans(editor.getHTML()))
          : ""
      ),
    [view, editor]
  )

  // Every opening starts at the gallery.
  useEffect(() => {
    if (open) return
    setView("gallery")
    setUnavailable(false)
    openedInitial.current = null
  }, [open])

  // An opening with an initial view goes straight to its template's wizard,
  // once the catalogue is there (when it fails to load, the gallery says so)
  // and the save made on opening has landed: the wizard reads the saved
  // questionnaire. While the save fails the gallery shows its banner and
  // Retry; a save that then succeeds opens the wizard.
  useEffect(() => {
    if (!open || !initialView || openedInitial.current === initialView) return
    if (saveState !== "saved") return
    if (!catalogue.data && !catalogue.isError) return
    openedInitial.current = initialView
    if (catalogue.isError) return
    const item = items.find((it) => sameRef(it, initialView.ref))
    if (item) {
      setUnavailable(false)
      setView({ wizard: item, editing: initialView.block })
    } else {
      setUnavailable(true)
    }
  }, [open, initialView, saveState, catalogue.data, catalogue.isError, items])

  const renderBody = () => {
    if (view !== "gallery") {
      const editingId = view.editing?.id
      // The block being edited does not clash with itself.
      const stagedNames = stagedBlockNames(
        stagedBlocks.filter((block) => block.id !== editingId),
        items
      )
      return (
        <TemplateWizard
          key={editingId ?? `${view.wizard.file}|${view.wizard.template}`}
          item={view.wizard}
          projectId={projectId}
          owner={owner}
          names={names}
          stagedNames={stagedNames}
          initial={view.editing}
          onAdd={(block) => {
            tray.update(block)
            if (initialView && block.id === initialView.block.id) {
              onProposalStaged?.(block.id)
            }
            setView("gallery")
          }}
          onBack={() => setView("gallery")}
        />
      )
    }
    if (catalogue.isLoading) {
      return (
        <div className="tpl-state">
          <Loader fontSize={28} />
        </div>
      )
    }
    if (catalogue.isError) {
      return (
        <div className="tpl-state">The templates could not be loaded.</div>
      )
    }
    if (items.length === 0) {
      return <div className="tpl-state">There are no templates yet.</div>
    }
    return (
      <TemplateGallery
        items={items}
        onUse={(item) => {
          setUnavailable(false)
          setView({ wizard: item })
        }}
        trayHasBlocks={stagedBlocks.length > 0}
        useBlockedLabel={
          saveState === "saving"
            ? "Saving…"
            : saveState === "failed"
              ? "Not saved"
              : undefined
        }
      />
    )
  }

  const count = stagedBlocks.length

  return (
    <>
      <Modal
        className="tpl-dialog"
        open={open}
        onCancel={requestClose}
        title="Templates"
        footer={null}
        width={1100}
        centered
        destroyOnHidden
        styles={{ body: { height: "76vh", padding: 0 } }}
      >
        {saveState === "failed" && (
          <div className="tpl-save-banner" role="alert">
            The questionnaire could not be saved. Templates need the saved
            questionnaire.
            {onRetrySave && (
              <Button onClick={onRetrySave}>Retry</Button>
            )}
          </div>
        )}
        {unavailable && view === "gallery" && (
          <div className="tpl-missing-banner" role="alert">
            That template is no longer available
          </div>
        )}
        {renderBody()}
        {view === "gallery" && count > 0 && (
          <StagingTray
            blocks={stagedBlocks}
            items={items}
            check={tray.check}
            inserting={inserting}
            onEdit={edit}
            onRemove={tray.remove}
            onMove={tray.move}
            onInsert={() => void insert()}
          />
        )}
      </Modal>
      <Modal
        className="tpl-confirm"
        open={confirmDiscard}
        title={`Discard ${count} staged ${count === 1 ? "block" : "blocks"}?`}
        onCancel={() => setConfirmDiscard(false)}
        width={380}
        centered
        footer={
          <>
            <Button onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </Button>
            <Button type="primary" danger onClick={discard}>
              Discard
            </Button>
          </>
        }
      >
        The blocks in the tray have not been inserted.
      </Modal>
    </>
  )
}

export default TemplatesDialog
